import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Loader2, X } from 'lucide-react';
import { cn } from '@/infrastructure/lib/utils';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useMagicFill } from '@/shared/contexts/MagicFillContext';
import { useFacets } from '../hooks/useFacets';
import type { Facet, FacetFilter } from '../types';

interface FacetSelectorProps {
  value: FacetFilter[];
  onChange: (value: FacetFilter[]) => void;
  organizationId?: string;
  className?: string;
  onlyDimension?: string;
  disabled?: boolean;
  /**
   * Quando passado, consome o MagicFillContext para mostrar spinner durante
   * o magic fill (global ou por campo) — mesmo comportamento dos demais
   * inputs com magic fill.
   */
  fieldKey?: string;
}

interface TreeNode {
  facet: Facet;
  children: TreeNode[];
}

/**
 * Componente reusável para escolher múltiplas facetas (PBI 79319 — Camada 2).
 *
 * Estratégia:
 *   - Constrói UMA árvore global a partir de parent_facet_id (não importa
 *     se o parent está em outra dimensão — cross-dim é suportado).
 *   - Os ROOTS (facetas sem parent) são agrupados por dimensão (name) e cada
 *     dimensão vira um header.
 *   - Filhos cross-dim aparecem aninhados sob o pai (ex: format=transcription
 *     aparece debaixo de type=meeting).
 *   - Auto-marca todos ancestrais quando filho é marcado.
 *   - Auto-desmarca descendentes quando pai é desmarcado.
 *   - Por padrão todos os nós com filhos começam EXPANDIDOS para o user ver
 *     a estrutura disponível.
 */
export function FacetSelector({ value, onChange, organizationId, className, onlyDimension, disabled, fieldKey }: FacetSelectorProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isGlobalLoading, isFieldLoading } = useMagicFill();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const orgId = organizationId ?? user?.organizationId ?? null;
  // Magic Fill loading: igual aos outros inputs (Input.tsx) — combina global + por campo.
  const magicFillLoading = fieldKey ? (isGlobalLoading || isFieldLoading(fieldKey)) : false;
  const effectiveDisabled = disabled || magicFillLoading;

  // Lista cacheada (React Query). Re-renders do formulário — frequentes durante
  // o progresso de embedding via SSE — servem do cache, sem refetch nem flicker.
  const { data: facets = [], isLoading: loading } = useFacets(orgId);

  const labelFor = (slug: string): string => {
    const key = `facets.labels.values.${slug}`;
    const translated = t(key);
    return translated === key ? slug : translated;
  };

  const dimensionLabel = (name: string): string => {
    const key = `facets.labels.${name}`;
    const translated = t(key);
    return translated === key ? name : translated;
  };

  const facetById = useMemo(() => {
    const m = new Map<string, Facet>();
    for (const f of facets) m.set(f.id, f);
    return m;
  }, [facets]);

  /**
   * Constrói árvore GLOBAL (cross-dim) e agrupa os ROOTS por dimensão.
   */
  const groupedRoots = useMemo<Array<{ name: string; roots: TreeNode[] }>>(() => {
    const filtered = onlyDimension ? facets.filter((f) => f.name === onlyDimension) : facets;

    // Index parent → children (global, sem filtrar por dimensão).
    const childrenByParent = new Map<string | 'root', Facet[]>();
    for (const f of filtered) {
      const parentExists = f.parent_facet_id && facetById.has(f.parent_facet_id);
      const key = parentExists ? f.parent_facet_id! : 'root';
      const list = childrenByParent.get(key) ?? [];
      list.push(f);
      childrenByParent.set(key, list);
    }

    const buildNode = (facet: Facet): TreeNode => ({
      facet,
      children: (childrenByParent.get(facet.id) ?? [])
        .slice()
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(buildNode),
    });

    // Roots: facetas sem parent (ou parent fora da lista filtrada). Agrupa por dimensão.
    const roots = (childrenByParent.get('root') ?? []).slice().sort((a, b) => a.value.localeCompare(b.value));
    const byDim = new Map<string, TreeNode[]>();
    for (const r of roots) {
      const arr = byDim.get(r.name) ?? [];
      arr.push(buildNode(r));
      byDim.set(r.name, arr);
    }
    return Array.from(byDim.entries())
      .map(([name, rootsArr]) => ({ name, roots: rootsArr }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [facets, facetById, onlyDimension]);

  const isSelected = (f: Facet) => value.some((v) => v.name === f.name && v.value === f.value);

  const ancestorsOf = (facet: Facet): Facet[] => {
    const out: Facet[] = [];
    let current = facet.parent_facet_id ? facetById.get(facet.parent_facet_id) : undefined;
    const visited = new Set<string>([facet.id]);
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      out.push(current);
      current = current.parent_facet_id ? facetById.get(current.parent_facet_id) : undefined;
    }
    return out;
  };

  const descendantsOf = (facet: Facet, include = false): Facet[] => {
    const out: Facet[] = [];
    if (include) out.push(facet);
    const stack: Facet[] = [facet];
    const visited = new Set<string>([facet.id]);
    while (stack.length > 0) {
      const cur = stack.pop()!;
      for (const f of facets) {
        if (f.parent_facet_id === cur.id && !visited.has(f.id)) {
          visited.add(f.id);
          out.push(f);
          stack.push(f);
        }
      }
    }
    return out;
  };

  const toggle = (f: Facet) => {
    if (disabled) return;
    if (isSelected(f)) {
      const toRemove = new Set<string>(
        descendantsOf(f, true).map((d) => `${d.name}:${d.value}`)
      );
      onChange(value.filter((v) => !toRemove.has(`${v.name}:${v.value}`)));
    } else {
      const toAdd: FacetFilter[] = [];
      for (const a of ancestorsOf(f)) {
        if (!isSelected(a)) toAdd.push({ name: a.name, value: a.value });
      }
      toAdd.push({ name: f.name, value: f.value });
      onChange([...value, ...toAdd]);
    }
  };

  const toggleCollapsed = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const removeBadge = (item: FacetFilter) => {
    if (disabled) return;
    const facet = facets.find((f) => f.name === item.name && f.value === item.value);
    if (facet) {
      toggle(facet);
    } else {
      onChange(value.filter((v) => !(v.name === item.name && v.value === item.value)));
    }
  };

  const renderNode = (node: TreeNode, depth: number): JSX.Element => {
    const hasChildren = node.children.length > 0;
    const isExpanded = !collapsed.has(node.facet.id);
    const checked = isSelected(node.facet);
    // Quando o filho está em DIMENSÃO DIFERENTE do pai, prefixa com a dimensão
    // pra deixar claro (ex: "Formato: Resumo" debaixo de "Reunião").
    const showDimensionPrefix = depth > 0 && (() => {
      const parentId = node.facet.parent_facet_id;
      if (!parentId) return false;
      const parent = facetById.get(parentId);
      return parent ? parent.name !== node.facet.name : false;
    })();
    return (
      <div key={node.facet.id} className="select-none">
        <div
          className="flex items-center gap-1.5 py-1 px-1 rounded hover:bg-accent/40"
          style={{ paddingLeft: 6 + depth * 16 }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleCollapsed(node.facet.id); }}
              onMouseDown={(e) => e.preventDefault()}
              className="text-muted-foreground hover:text-foreground p-0.5 inline-flex"
              aria-label={isExpanded ? 'collapse' : 'expand'}
            >
              {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          ) : (
            <span className="inline-block w-[18px]" />
          )}
          <Checkbox
            id={`facet-${node.facet.id}`}
            checked={checked}
            onCheckedChange={() => toggle(node.facet)}
            disabled={disabled}
          />
          <label htmlFor={`facet-${node.facet.id}`} className="text-sm cursor-pointer flex-1 py-0.5">
            {showDimensionPrefix && (
              <span className="text-muted-foreground text-xs mr-1">{dimensionLabel(node.facet.name)}:</span>
            )}
            <span className="font-mono text-xs">{node.facet.value}</span>
            <span className="text-muted-foreground ml-2">({labelFor(node.facet.value)})</span>
          </label>
        </div>
        {hasChildren && isExpanded && (
          <div>{node.children.map((c) => renderNode(c, depth + 1))}</div>
        )}
      </div>
    );
  };

  const hasAny = facets.length > 0;
  const placeholder = loading ? '…' : (!hasAny ? t('facets.selectorEmpty') : t('facets.selectorPlaceholder'));

  return (
    <div className={className}>
      <Popover open={open && !magicFillLoading} onOpenChange={(o) => !magicFillLoading && setOpen(o)}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            disabled={effectiveDisabled || !hasAny}
            className={cn(
              'w-full justify-between min-h-[40px] h-auto py-1.5',
              magicFillLoading && 'opacity-50'
            )}
          >
            <div className="flex flex-wrap gap-1 items-center">
              {value.length === 0 ? (
                <span className="text-muted-foreground text-sm">{placeholder}</span>
              ) : (
                value.map((item) => (
                  <Badge
                    key={`${item.name}:${item.value}`}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    <span>{dimensionLabel(item.name)}: {labelFor(item.value)}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { if (!effectiveDisabled) { e.stopPropagation(); removeBadge(item); } }}
                      onKeyDown={(e) => { if (!effectiveDisabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); removeBadge(item); } }}
                      className="ml-0.5 inline-flex items-center justify-center rounded hover:bg-muted-foreground/20 cursor-pointer"
                      aria-label={`remove ${item.name}:${item.value}`}
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </Badge>
                ))
              )}
            </div>
            {magicFillLoading
              ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />
              : <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn('p-0 w-[--radix-popover-trigger-width]')}
          align="start"
          // Importante: o Popover do shadcn usa Portal — não corta com overflow do dialog.
        >
          <div className="max-h-[360px] overflow-y-auto py-2">
            {groupedRoots.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">{t('facets.selectorEmpty')}</div>
            ) : (
              groupedRoots.map((group) => (
                <div key={group.name} className="mb-2 last:mb-0">
                  <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {dimensionLabel(group.name)}
                  </div>
                  <div>
                    {group.roots.map((node) => renderNode(node, 0))}
                  </div>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
